import { type ChangeEvent } from "react";
import { Label } from "../common/Controls.js";

function StepperButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label === "+" ? "Increase mines" : "Decrease mines"}
      style={{
        width: 22,
        height: 18,
        padding: 0,
        background: "var(--bg-2)",
        border: "1px solid var(--line)",
        borderRadius: 2,
        color: disabled ? "var(--fg-4)" : "var(--fg-1)",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        lineHeight: 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

/**
 * Pressure-Plate-specific dense controls. The shared atoms (Label, BetInput,
 * PrimaryCta, FairnessFooter, Wallet, MuteToggle, SectionHeader, Stat) are
 * hoisted to ../common/Controls.tsx and re-exported below for backwards
 * compatibility with existing PressurePlate.tsx imports.
 *
 * What stays here: components that bake in Pressure-Plate semantics
 * (MultiplierDisplay's flash-on-pick mode set, MineCountPicker's 1-24 slider
 * with the projection curve preview, StatusPill's mine-count subtitle).
 */

// Re-export hoisted shared atoms so existing imports `from "./Controls.js"` keep working.
export {
  Label,
  BetInput,
  PrimaryCta,
  FairnessFooter,
  Wallet,
  MuteToggle,
  SectionHeader,
  Stat,
} from "../common/Controls.js";

// ---- MultiplierDisplay ---------------------------------------------------
export type MultiplierMode = "idle" | "live" | "win" | "bust";

const SIZES: Record<"sm" | "md" | "lg" | "xl", { f: number; ls: number }> = {
  sm: { f: 56, ls: -2 },
  md: { f: 96, ls: -3 },
  lg: { f: 156, ls: -5 },
  xl: { f: 220, ls: -7 },
};

export function MultiplierDisplay({
  value = 1.0,
  mode = "idle",
  size = "lg",
  flashKey = 0,
}: {
  value?: number;
  mode?: MultiplierMode;
  size?: "sm" | "md" | "lg" | "xl";
  flashKey?: number;
}) {
  const sizes = SIZES[size];
  let color: string;
  let glow: string;
  if (mode === "win") {
    color = "var(--green-bright)";
    glow = "var(--green)";
  } else if (mode === "bust") {
    color = "var(--red-bright)";
    glow = "var(--red)";
  } else if (mode === "live") {
    color = "var(--gold-bright)";
    glow = "var(--gold)";
  } else {
    color = "var(--fg-2)";
    glow = "var(--gold-deep)";
  }
  return (
    <div
      key={flashKey}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: sizes.f,
        letterSpacing: sizes.ls,
        lineHeight: 0.85,
        color,
        textShadow: `0 0 ${sizes.f * 0.1}px ${glow}, 0 0 ${sizes.f * 0.3}px ${glow}88`,
        whiteSpace: "nowrap",
        animation: flashKey ? "pp-bump 280ms cubic-bezier(.2,.8,.3,1.2)" : "none",
        fontVariantNumeric: "tabular-nums slashed-zero",
      }}
    >
      {value.toFixed(2)}
      <span style={{ fontSize: sizes.f * 0.55, marginLeft: sizes.f * 0.04 }}>×</span>
    </div>
  );
}

// ---- MineCountPicker -----------------------------------------------------
// `projection` MUST match the engine's `multiplierAfter` — same 5% house
// edge so the picker preview equals what the player actually gets paid.
// Sync with apps/web/src/games/pressure-plate/engine.ts and
// packages/engine/src/games/pressure-plate.ts when changing.
const HOUSE_EDGE = 0.05;
function projection(mines: number, safePicks = 5): number {
  let p = 1;
  for (let i = 0; i < safePicks; i += 1) p *= (25 - i) / (25 - i - mines);
  return p * (1 - HOUSE_EDGE);
}

/** Risk-tier presets — modeled on real Mines variants on Stake/BCGame where
 *  3/5/10/18 are the most-played counts. Each tier maps a recognizable
 *  "skill cue" to a mine count so a new player picks intent (Easy/Med/
 *  Hard/Insane) instead of an arbitrary number. */
const TIER_PRESETS: ReadonlyArray<{ value: number; label: string; tone: "green" | "gold" | "red" | "purple" }> = [
  { value: 3, label: "EASY", tone: "green" },
  { value: 5, label: "MED", tone: "gold" },
  { value: 10, label: "HARD", tone: "red" },
  { value: 18, label: "INSANE", tone: "purple" },
];

const TONE_COLORS: Record<"green" | "gold" | "red" | "purple", { fg: string; bg: string; border: string }> = {
  green: { fg: "var(--green-bright)", bg: "rgba(120,255,140,0.10)", border: "rgba(120,255,140,0.55)" },
  gold: { fg: "var(--gold-bright)", bg: "rgba(241,203,107,0.10)", border: "var(--gold-bright)" },
  red: { fg: "var(--red-bright)", bg: "rgba(255,90,90,0.10)", border: "rgba(255,90,90,0.55)" },
  purple: { fg: "#C690E8", bg: "rgba(198,144,232,0.10)", border: "rgba(198,144,232,0.55)" },
};

export function MineCountPicker({
  value = 5,
  onChange,
  collapsed = false,
  disabled = false,
}: {
  value?: number;
  onChange?: (v: number) => void;
  collapsed?: boolean;
  disabled?: boolean;
}) {
  const showcase = projection(value);

  if (collapsed) {
    // Compact preset-chip selector — replaces the legacy <select> dropdown
    // that read like a label, not a control. Four risk tiers cover ~95% of
    // play; values outside the presets (e.g., 7 mines) stay reachable via
    // the +/− stepper without bloating the rail.
    const isPreset = TIER_PRESETS.some((t) => t.value === value);
    const handleStep = (delta: number): void => {
      if (disabled) return;
      const next = Math.max(1, Math.min(24, value + delta));
      if (next !== value) onChange?.(next);
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: disabled ? 0.55 : 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Label>Mines</Label>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--gold-bright)",
              letterSpacing: 0.5,
            }}
          >
            {value} · {showcase.toFixed(2)}× @5
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
          {TIER_PRESETS.map((t) => {
            const active = value === t.value;
            const colors = TONE_COLORS[t.tone];
            return (
              <button
                key={t.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange?.(t.value)}
                aria-pressed={active}
                style={{
                  padding: "5px 4px",
                  background: active ? colors.bg : "var(--bg-2)",
                  border: `1px solid ${active ? colors.border : "var(--line)"}`,
                  borderRadius: 3,
                  cursor: disabled ? "not-allowed" : "pointer",
                  fontFamily: "var(--font-ui)",
                  color: active ? colors.fg : "var(--fg-2)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  transition: "background 150ms, border-color 150ms",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 16,
                    letterSpacing: 0,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {t.value}
                </span>
                <span
                  style={{
                    fontSize: 7,
                    letterSpacing: 1,
                    fontWeight: 700,
                    color: active ? colors.fg : "var(--fg-4)",
                  }}
                >
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
        {/* Fine-tune stepper. Stays out of the way for preset users; power
            users tap +/− to land on uncommon counts (7, 12, 24...). */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
            padding: "3px 6px",
            background: isPreset ? "transparent" : "rgba(241,203,107,0.06)",
            border: `1px dashed ${isPreset ? "var(--line)" : "rgba(241,203,107,0.4)"}`,
            borderRadius: 3,
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 9,
              letterSpacing: 1,
              color: "var(--fg-4)",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {isPreset ? "Custom" : "Custom · " + value}
          </span>
          <div style={{ display: "flex", gap: 3 }}>
            <StepperButton onClick={() => handleStep(-1)} disabled={disabled || value <= 1} label="−" />
            <StepperButton onClick={() => handleStep(1)} disabled={disabled || value >= 24} label="+" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Label>Mines</Label>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 28,
              color: "var(--gold-bright)",
              letterSpacing: -1,
              lineHeight: 1,
            }}
          >
            {value}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--fg-3)",
              letterSpacing: 1,
            }}
          >
            / 25
          </span>
        </div>
      </div>

      <div
        style={{
          position: "relative",
          height: 56,
          padding: "8px 10px",
          background: "var(--bg-2)",
          border: "1px solid var(--line)",
          borderRadius: 3,
        }}
      >
        <svg
          viewBox="0 0 240 40"
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id="ppc-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="var(--gold-deep)" />
              <stop offset="1" stopColor="var(--gold-bright)" />
            </linearGradient>
          </defs>
          <line x1="0" y1="40" x2="240" y2="40" stroke="var(--line)" strokeWidth="1" />
          <path
            d={(() => {
              const pts: string[] = [];
              for (let m = 1; m <= 24; m += 1) {
                const p = projection(m);
                const x = ((m - 1) / 23) * 240;
                const y = 40 - Math.min(38, Math.log(p + 1) * 7);
                pts.push(`${pts.length === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`);
              }
              return pts.join(" ");
            })()}
            stroke="url(#ppc-grad)"
            strokeWidth="1.4"
            fill="none"
          />
          <circle
            cx={((value - 1) / 23) * 240}
            cy={40 - Math.min(38, Math.log(projection(value) + 1) * 7)}
            r="3"
            fill="var(--gold-bright)"
          />
        </svg>
      </div>

      <input
        type="range"
        min="1"
        max="24"
        step="1"
        value={value}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange?.(Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--gold-bright)", height: 4 }}
        aria-label="Number of mines"
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 6,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
        }}
      >
        {[
          { l: "Safe", v: String(25 - value), c: "var(--green-bright)" },
          { l: "@5 picks", v: `${projection(value, 5).toFixed(2)}×`, c: "var(--gold-bright)" },
          { l: "Hit %", v: `${((value / 25) * 100).toFixed(1)}%`, c: "var(--red-bright)" },
        ].map((s) => (
          <div
            key={s.l}
            style={{
              background: "var(--bg-2)",
              border: "1px solid var(--line)",
              borderRadius: 2,
              padding: "6px 8px",
            }}
          >
            <div
              style={{
                color: "var(--fg-3)",
                fontSize: 9,
                letterSpacing: 1,
                textTransform: "uppercase",
                fontFamily: "var(--font-ui)",
                fontWeight: 700,
              }}
            >
              {s.l}
            </div>
            <div style={{ color: s.c, fontWeight: 600, marginTop: 2 }}>{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- StatusPill ---------------------------------------------------------
export type Scene = "idle" | "midround" | "bust" | "win";

const PILL_CFG: Record<Scene, { l: string; c: string; d: string }> = {
  idle: { l: "AWAITING DEPLOY", c: "var(--fg-2)", d: "var(--fg-3)" },
  midround: { l: "ROUND LIVE", c: "var(--cyan-bright)", d: "var(--cyan)" },
  bust: { l: "ALARM TRIPPED", c: "var(--red-bright)", d: "var(--red)" },
  win: { l: "PAYOUT LOCKED", c: "var(--green-bright)", d: "var(--green)" },
};

export function StatusPill({
  scene,
  mineCount,
}: {
  scene: Scene;
  mineCount: number;
}) {
  const cfg = PILL_CFG[scene];
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 12px",
        background: "rgba(11,14,22,0.7)",
        border: `1px solid ${cfg.d}55`,
        borderRadius: 2,
        fontFamily: "var(--font-ui)",
        fontSize: 10,
        fontWeight: 800,
        color: cfg.c,
        letterSpacing: 2.5,
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.c,
          boxShadow: `0 0 8px ${cfg.d}`,
        }}
      />
      {cfg.l}
      <span
        style={{
          color: "var(--fg-4)",
          fontWeight: 500,
          letterSpacing: 1,
          marginLeft: 6,
        }}
      >
        · {mineCount} mines
      </span>
    </div>
  );
}
